/** The shared human campaign screen; canonical fundraiser rank is the authority. */
import { useRef, useState, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isGivmoError,
  sdk,
  type P2pConfigOut,
  type P2pConfigRequest,
  type P2pJoinPolicy,
  type P2pManagedSupporterOut,
  type P2pSupporterStatus,
  type P2pTeamCreateRequest,
  type P2pTeamStatus,
  type P2pTeamUpdateRequest,
  type P2pTeamRowOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  EmptyState,
  Icon,
  RadioTile,
  RadioTileGroup,
  RowActions,
  Switch,
  TextField,
  Textarea,
  cn,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import {
  Copy,
  Info,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  UserRoundX,
  Users,
  XCircle,
} from 'lucide-react';
import { useFundraisersModule } from './fundraisers-context';
import { publicJoinUrl } from './fundraisers-routes';
import { campaignOptions, publicLeaderboardOptions } from './campaign-queries';
import { FundraisersLoading } from './fundraisers-state';
import {
  displayP2pDate,
  displayP2pMoney,
  pageActiveLabel,
  supporterSourceLabel,
  supporterStatusLabel,
  supporterStatusTone,
  teamStatusLabel,
  teamStatusTone,
  type ManagedP2pTeamRow,
} from './p2p-decode';
import { CampaignInvites } from './campaign-invites';
import { useLivePage } from './use-live-page';
import { CardStyleCard, DeadlineCard, WhoPicksCard } from './campaign-settings-cards';
import { LAYERS_COPY } from './layers-copy';
import { endsLive, layersLive, recipientChoiceLive } from './layers-decode';
import { layerParentOptions, publicCampaignOptions } from './layers-queries';
import { PledgeTemplateCard } from './pledge-template-card';
import { seasonLive } from './season-setup-decode';
import { JOIN_POLICY_ORDER, REQUESTS_COPY, type JoinPolicyValue } from './join-requests-copy';
import { PeopleRequests } from './join-requests';
import { StateNotice } from './lifecycle-ui';
import { SuggestedPageGoal } from './suggested-page-goal';

const PREFIX = 'p2p-campaign';
type TeamDialog =
  | { kind: 'create' }
  | { kind: 'rename' | 'close' | 'reopen' | 'remove' | 'reinstate'; team: ManagedP2pTeamRow }
  | null;
/** What the team dialog's fields hold when its confirm is pressed. */
interface TeamDraft {
  name: string;
  description: string;
}
/** A team dialog's draft as typed, kept against the one open it belongs to. */
interface StashedTeamDraft {
  dialog: NonNullable<TeamDialog>;
  draft: TeamDraft;
}
interface SupporterDialog {
  supporter: P2pManagedSupporterOut;
  nextStatus: P2pSupporterStatus;
}
interface TeamUpdateVariables {
  team: ManagedP2pTeamRow;
  body: P2pTeamUpdateRequest;
}
type IndexedTeamRow = ManagedP2pTeamRow & { rowIndex: number };

const LEADERBOARD_COLUMNS: DataTableColumn<P2pTeamRowOut>[] = [
  {
    id: 'rank',
    header: 'Rank',
    align: 'right',
    numeric: true,
    cell: (team) => String(team.rank),
  },
  {
    id: 'name',
    header: 'Team',
    width: '34%',
    cell: (team) => <span className="font-w600 text-text-strong">{team.name}</span>,
  },
  {
    id: 'raised',
    header: 'Raised',
    align: 'right',
    numeric: true,
    cell: (team) => displayP2pMoney(team.raised_amount),
  },
  {
    id: 'donors',
    header: 'Donors',
    align: 'right',
    numeric: true,
    cell: (team) => String(team.donor_count),
  },
  {
    id: 'members',
    header: 'Supporters',
    align: 'right',
    numeric: true,
    cell: (team) => String(team.member_count),
  },
];

/** What the Campaign tab reads off the fundraiser itself (FCM-WEB25). */
export interface CampaignPageFacts {
  name: string;
  state: string;
  /** `lifecycle.close_reason` on a closed row (`campaign_ended`: a campaign's end closed it). */
  closeReason: string | null;
  /** The campaign's own goal, a decimal money string. */
  goal: string | null;
  /** The names of its own charities. */
  charities: readonly string[];
  /** Gifts to its own page (not its supporters' or layers'), from the private read; null until read. */
  ownDonations: number | null;
  /** A draft a campaign above ended (FCM-BE35): it reads ended, though its state is still Draft. */
  chainEnded?: boolean;
}

export function P2PCampaignPage({
  campaignId,
  canManage,
  enabled = true,
  facts,
  onSaved,
}: {
  campaignId: string;
  canManage: boolean;
  enabled?: boolean;
  /** The fundraiser's own facts; without them (an older host) the tab draws no layer cards. */
  facts?: CampaignPageFacts;
  /** Re-read the fundraiser after a settings write that moves its state (a deadline's end). */
  onSaved?: () => Promise<unknown>;
}) {
  const config = useFundraisersModule();
  const queryClient = useQueryClient();
  const options = campaignOptions(campaignId);
  const overviewQuery = useQuery({ ...options, enabled });
  // FCM-WEB25: where it sits, for who picks the charities above it (the layer reads, dark == missing).
  const layerConfig = overviewQuery.data?.config;
  const parentQuery = useQuery({
    ...layerParentOptions(campaignId),
    enabled: enabled && layersLive(layerConfig) && recipientChoiceLive(layerConfig),
    retry: false,
  });
  const ancestors = parentQuery.data?.ancestors ?? [];
  const directParent = ancestors[ancestors.length - 1] ?? null;
  // A campaign above locks the charity choice, so the card waits for the answer rather than offer a
  // choice the server would refuse (a read that isn't made, or fails, leaves no campaign above). Only
  // the first answer is waited for: a re-read keeps the card, so a save's own re-read never takes it
  // away, with its confirmation (FCM-WEB61).
  const parentKnown = !parentQuery.isLoading;
  const parentCampaign = useQuery({
    ...publicCampaignOptions(directParent?.campaign_id ?? ''),
    enabled: directParent !== null,
    retry: false,
  });
  const publicOptions = publicLeaderboardOptions(campaignId);
  const leaderboardQuery = useQuery({
    ...publicOptions,
    enabled: enabled && overviewQuery.isSuccess,
  });
  const live = useLivePage();
  // A cached role alone cannot enable a dark, failed or mismatched overview.
  const allowWrites = canManage && enabled && overviewQuery.isSuccess && overviewQuery.data != null;
  // FCM-WEB25, settled against the ruled ended frames 12 and 12b: an ended campaign's settings are its
  // record (option C), so joining, teams, the charity choice, the card's look and join invitations stop
  // being editable; its way back stays (its own deadline, frame 12's "Change deadline") and so does the
  // moderation of a supporter's page, which its public page still shows.
  const ended = facts?.state === 'closed' || facts?.chainEnded === true;
  const allowSettings = allowWrites && !ended;
  const [teamDialog, setTeamDialog] = useState<TeamDialog>(null);
  // The team dialog's draft as typed. A ref, so a keystroke never re-renders this page (FCM-WEB14).
  // The dialog unmounts while this page still holds it open when writes stop being available or the
  // overview read fails, and mounts again when they return; it restores the draft from here, as it
  // did when the draft was page state. Any close clears it.
  const teamDraft = useRef<StashedTeamDraft | null>(null);
  const closeTeamDialog = () => {
    teamDraft.current = null;
    setTeamDialog(null);
  };
  const [supporterDialog, setSupporterDialog] = useState<SupporterDialog | null>(null);
  const invalidateOverview = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: options.queryKey }),
      queryClient.invalidateQueries({ queryKey: publicOptions.queryKey }),
    ]);
  const settingsSaved = () => Promise.all([invalidateOverview(), onSaved?.()]);
  function requireWrite() {
    if (!allowWrites) throw new Error('Campaign changes are not available.');
  }
  function failed(error: unknown) {
    if (live())
      toast.error(
        isGivmoError(error) ? error.message : 'Couldn’t save this change. Please try again.',
      );
  }
  const updateConfig = useMutation({
    mutationFn: async (body: P2pConfigRequest) => {
      requireWrite();
      return sdk.campaignsUpdateCampaign({
        path: { fundraiser_id: campaignId },
        body,
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await invalidateOverview();
      if (live()) toast.success('Campaign settings updated');
    },
    onError: failed,
  });
  const createTeam = useMutation({
    mutationFn: async (body: P2pTeamCreateRequest) => {
      requireWrite();
      return sdk.campaignsCreateCampaignTeam({
        path: { fundraiser_id: campaignId },
        body,
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await invalidateOverview();
      if (live()) {
        closeTeamDialog();
        toast.success('Team created');
      }
    },
    onError: failed,
  });
  const updateTeam = useMutation({
    mutationFn: async ({ team, body }: TeamUpdateVariables) => {
      requireWrite();
      return sdk.campaignsUpdateCampaignTeam({
        path: { fundraiser_id: campaignId, team_id: team.team_id },
        body,
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await invalidateOverview();
      if (live()) {
        closeTeamDialog();
        toast.success('Team updated');
      }
    },
    onError: failed,
  });
  const updateSupporter = useMutation({
    mutationFn: async ({ supporter, nextStatus }: SupporterDialog) => {
      requireWrite();
      return sdk.campaignsUpdateCampaignSupporter({
        path: { fundraiser_id: campaignId, supporter_id: supporter.supporter_id },
        body: { status: nextStatus },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await invalidateOverview();
      if (live()) {
        setSupporterDialog(null);
        toast.success('Supporter status updated');
      }
    },
    onError: failed,
  });

  if (overviewQuery.isPending) return <FundraisersLoading />;
  if (overviewQuery.isError || !overviewQuery.data)
    return (
      <EmptyState
        testId={`${PREFIX}-error`}
        title="Campaign management isn’t available"
        description="The campaign could not be loaded."
        action={
          <Button
            appearance="saas"
            tone="secondary"
            testId={`${PREFIX}-retry`}
            onClick={() => void overviewQuery.refetch()}
          >
            Try again
          </Button>
        }
      />
    );
  const overview = overviewQuery.data;
  // The recipient choice's lock, read off the same facts as the server's (its settings read names none).
  const lockedBelow =
    overview.supporters.length > 0 ||
    (overview.layers?.length ?? 0) > 0 ||
    (facts?.ownDonations ?? 0) > 0;
  // Frame 12: a campaign its OWN deadline ended (no campaign above's end) reopens by moving it.
  const endedOnOwnDeadline =
    ended &&
    facts?.closeReason === 'campaign_ended' &&
    overview.config.ends_with == null &&
    overview.config.deadline_at != null;
  const teams = overview.teams;
  // With teams off nothing new can start: no new team, and "Fundraisers can create teams" waits, off.
  const teamsOn = overview.config.teams_enabled;
  const teamRows = teams.map((team, rowIndex) => ({ ...team, rowIndex }));
  const joinUrl = publicJoinUrl(config.links.donateBaseUrl, campaignId);
  // FCM-WEB33 (D-FCM-35): people's requests show while the campaign takes them or some still wait;
  // the shutter's and the end's notices speak of them only then (frames 01b, 01c).
  const asksFirst = overview.config.join_policy === 'request_to_join';
  const requestsShown = asksFirst || (overview.pendingRequests?.people ?? 0) > 0;
  function openCreateTeam() {
    if (!allowSettings || !teamsOn) return;
    setTeamDialog({ kind: 'create' });
  }
  function openTeamDialog(
    kind: Exclude<NonNullable<TeamDialog>['kind'], 'create'>,
    team: ManagedP2pTeamRow,
  ) {
    if (!allowSettings) return;
    setTeamDialog({ kind, team });
  }
  function confirmTeamDialog(draft: TeamDraft) {
    if (!allowSettings || !teamDialog || createTeam.isPending || updateTeam.isPending) return;
    const name = draft.name.trim();
    if (teamDialog.kind === 'create') {
      if (name) createTeam.mutate({ name, description: draft.description.trim() || null });
      return;
    }
    if (teamDialog.kind === 'rename') {
      if (name && name !== teamDialog.team.name)
        updateTeam.mutate({ team: teamDialog.team, body: { name } });
      return;
    }
    const next: Record<Exclude<typeof teamDialog.kind, 'rename'>, P2pTeamStatus> = {
      close: 'closed',
      reopen: 'active',
      remove: 'removed',
      reinstate: 'active',
    };
    updateTeam.mutate({ team: teamDialog.team, body: { status: next[teamDialog.kind] } });
  }
  async function copyJoinUrl() {
    try {
      await navigator.clipboard.writeText(joinUrl);
      if (live()) toast.success('Join link copied');
    } catch {
      if (live()) toast.error('Could not copy the join link. Copy it manually instead.');
    }
  }
  function supporterActions(supporter: P2pManagedSupporterOut, mobile = false) {
    if (!allowWrites || (supporter.status !== 'active' && supporter.status !== 'removed'))
      return null;
    const removed = supporter.status === 'removed';
    return (
      <RowActions
        label={`Actions for ${supporter.display_name}`}
        triggerText={mobile ? 'Manage fundraiser' : undefined}
        contentClassName="fundraisers-menu"
        testId={`p2p-supporter-actions-${mobile ? 'mobile-' : ''}${supporter.supporter_id}`}
        actions={[
          {
            label: removed ? 'Reinstate supporter' : 'Detach supporter',
            destructive: !removed,
            icon: <Icon icon={removed ? RotateCcw : UserRoundX} size={14} aria-hidden />,
            testId: removed
              ? `p2p-supporter-reinstate-${supporter.supporter_id}`
              : `p2p-supporter-detach-${supporter.supporter_id}`,
            onSelect: () =>
              setSupporterDialog({ supporter, nextStatus: removed ? 'active' : 'removed' }),
          },
        ]}
      />
    );
  }
  const teamColumns: DataTableColumn<IndexedTeamRow>[] = [
    {
      id: 'name',
      header: 'Team',
      stacked: 'heading',
      width: '34%',
      cell: (team) => <span className="font-w600 text-text-strong">{team.name}</span>,
    },
    {
      id: 'members',
      header: 'Supporters',
      align: 'right',
      numeric: true,
      cell: (team) => String(team.member_count),
    },
    {
      id: 'raised',
      header: 'Raised',
      align: 'right',
      numeric: true,
      cell: (team) => displayP2pMoney(team.raised_amount),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (team) => (
        <Badge tone={teamStatusTone(team.status)}>{teamStatusLabel(team.status)}</Badge>
      ),
    },
    ...(allowSettings
      ? [
          {
            id: 'actions',
            header: '',
            align: 'right' as const,
            cell: (team: IndexedTeamRow) => (
              <TeamActions team={team} index={team.rowIndex} onAction={openTeamDialog} />
            ),
          },
        ]
      : []),
  ];
  const supporterColumns: DataTableColumn<P2pManagedSupporterOut>[] = [
    {
      id: 'name',
      header: 'Supporter',
      width: '30%',
      cell: (supporter) => (
        <div className="flex flex-col gap-4">
          <span className="font-w600 text-text-strong">{supporter.display_name}</span>
          <span className="text-s12 text-text-secondary">
            {supporterSourceLabel(supporter.source)} · {displayP2pDate(supporter.joined_at)}
          </span>
        </div>
      ),
    },
    { id: 'team', header: 'Team', cell: (supporter) => supporter.team_name || 'Solo' },
    {
      id: 'raised',
      header: 'Raised',
      align: 'right',
      numeric: true,
      cell: (supporter) => (
        <div className="flex flex-col gap-4">
          <span>{displayP2pMoney(supporter.raised_amount)}</span>
          {/* "1 donor" for one (FCM-WEB43). The count and its word stay two text nodes, as before. */}
          <span className="text-s12 text-text-secondary">
            {supporter.donor_count}
            {supporter.donor_count === 1 ? ' donor' : ' donors'}
          </span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (supporter) => (
        <div className="flex flex-col items-start gap-4">
          <Badge tone={supporterStatusTone(supporter.status)}>
            {supporterStatusLabel(supporter.status)}
          </Badge>
          <SupporterPageState supporter={supporter} />
        </div>
      ),
    },
    ...(allowWrites
      ? [
          {
            id: 'actions',
            header: '',
            align: 'right' as const,
            cell: (supporter: P2pManagedSupporterOut) => supporterActions(supporter),
          },
        ]
      : []),
  ];
  return (
    <div className="flex min-w-0 flex-col gap-20" data-testid="page-p2p-campaign">
      <Card flush testId={`${PREFIX}-config-card`}>
        <CardHeader title="Joining" />
        <CardBody className="flex flex-col gap-20">
          <form
            className="flex flex-col gap-20"
            data-testid={allowSettings ? 'p2p-config-form' : undefined}
            onSubmit={(event) => event.preventDefault()}
          >
            <div className="flex flex-col gap-8">
              <ConfigSwitch
                label={REQUESTS_COPY.shutterLabel}
                field="join_open"
                // An ended campaign takes no one, whatever its switch was left at (frame 01c).
                checked={overview.config.join_open && !ended}
                canManage={allowSettings}
                pending={updateConfig.isPending}
                onChange={(body) => updateConfig.mutate(body)}
              />
              {ended && asksFirst ? (
                <StateNotice icon={Info} testId="p2p-join-ended-notice" action={null}>
                  {REQUESTS_COPY.endedLine}
                </StateNotice>
              ) : !ended && !overview.config.join_open && requestsShown ? (
                <StateNotice icon={Info} testId="p2p-join-paused-notice" action={null}>
                  {REQUESTS_COPY.shutterPaused}
                </StateNotice>
              ) : null}
            </div>
            <JoinPolicy
              config={overview.config}
              canManage={allowSettings}
              pending={updateConfig.isPending}
              pendingValue={updateConfig.isPending ? updateConfig.variables?.join_policy : null}
              membersWords={config.flavor.joinMembers ?? null}
              layersShown={layersLive(overview.config)}
              onChange={(body) => updateConfig.mutate(body)}
            />
            {/* EMC-WEB6: what a fundraiser page under this campaign starts its goal with. */}
            <SuggestedPageGoal
              config={overview.config}
              canManage={allowSettings}
              pending={updateConfig.isPending}
              onChange={(body) => updateConfig.mutate(body)}
            />
          </form>
          {/* An ended campaign admits no new supporter (the join's "This campaign has ended"). */}
          {!ended && (
            <div
              className="flex items-center gap-8 rounded-lg border border-border-subtle px-12 py-10"
              data-testid="p2p-join-url"
            >
              <span
                className="min-w-0 flex-1 truncate text-s13 text-text-secondary"
                title={joinUrl}
              >
                {joinUrl}
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                leadingIcon={<Icon icon={Copy} size={14} aria-hidden />}
                testId="p2p-join-url-copy"
                onClick={() => void copyJoinUrl()}
              >
                Copy join link
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
      {requestsShown && (
        <PeopleRequests
          campaignId={campaignId}
          campaignName={facts?.name ?? ''}
          canAnswer={allowWrites}
          ended={ended}
        />
      )}
      {facts && endsLive(overview.config) && (
        <DeadlineCard
          campaignId={campaignId}
          config={overview.config}
          // The way back for a campaign its own deadline ended (frame 12): move it later or clear it.
          editable={allowWrites && (!ended || endedOnOwnDeadline)}
          ended={ended}
          onSaved={settingsSaved}
        />
      )}
      {facts && recipientChoiceLive(overview.config) && parentKnown && (
        <WhoPicksCard
          campaignId={campaignId}
          campaignName={facts.name}
          config={overview.config}
          editable={allowSettings}
          locked={lockedBelow || directParent !== null}
          lockedByAbove={!lockedBelow && directParent !== null}
          draft={facts.state === 'draft'}
          ownCharities={facts.charities}
          bound={
            directParent !== null && parentCampaign.data?.recipient_choice === 'campaign'
              ? { parent: directParent.campaign_name }
              : null
          }
          onSaved={settingsSaved}
        />
      )}
      {facts && layersLive(overview.config) && (
        <CardStyleCard
          campaignId={campaignId}
          campaignName={facts.name}
          config={overview.config}
          editable={allowSettings}
          draft={facts.state === 'draft'}
          goal={facts.goal}
          onSaved={settingsSaved}
        />
      )}
      {/* FCM-WEB34 (D-FCM-61's decision 3, D-FCM-53): the pledge this campaign adds to the pages and games
          under it, its OWNER rank's (the template's read answers that rank only). */}
      {facts && canManage && seasonLive(overview.config) && (
        <PledgeTemplateCard
          campaignId={campaignId}
          campaignName={facts.name}
          editable={allowSettings}
          examplePerson={overview.supporters[0]?.display_name ?? null}
          config={overview.config}
          ownCharities={facts.charities}
        />
      )}
      <section className="flex flex-col gap-12" data-testid={`${PREFIX}-teams-card`}>
        <div className="flex items-center justify-between gap-12">
          <div className="min-w-0">
            <h2 className="text-s16 font-w600 text-text-strong">Teams</h2>
            {/* F13: which kind of team, while campaigns can have layers. */}
            {overview.layers !== null && (
              <p
                className="mt-2 text-s13 leading-h15 text-text-secondary"
                data-testid="p2p-teams-helper"
              >
                {LAYERS_COPY.teamsHelper}
              </p>
            )}
          </div>
          {allowSettings && teamsOn && (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
              testId="p2p-team-create"
              onClick={openCreateTeam}
            >
              New team
            </Button>
          )}
        </div>
        <Card flush>
          <CardBody className="flex flex-col gap-16">
            <ConfigSwitch
              label="Teams enabled"
              field="teams_enabled"
              checked={teamsOn}
              canManage={allowSettings}
              pending={updateConfig.isPending}
              onChange={(body) => updateConfig.mutate(body)}
            />
            <ConfigSwitch
              label="Fundraisers can create teams"
              field="supporter_teams_allowed"
              checked={teamsOn && overview.config.supporter_teams_allowed}
              canManage={allowSettings}
              pending={updateConfig.isPending}
              disabled={!teamsOn}
              onChange={(body) => updateConfig.mutate(body)}
            />
          </CardBody>
        </Card>
        <div className="hidden lg:block">
          <DataTable
            testId={`${PREFIX}-teams`}
            columns={teamColumns}
            rows={teamRows}
            rowKey={(team) => team.team_id}
            rowTestId={(team) => `p2p-team-row-${team.rowIndex}`}
            emptyState={
              <EmptyState
                testId={`${PREFIX}-teams-empty`}
                title="No teams yet"
                description={
                  ended || !teamsOn
                    ? undefined
                    : 'Supporters can fundraise solo, or you can create a team.'
                }
              />
            }
          />
        </div>
        <div className="flex flex-col gap-12 lg:hidden" data-testid="p2p-team-cards">
          {teamRows.map((team) => (
            <Card key={team.team_id} flush>
              <CardBody className="flex flex-col gap-16 p-16">
                <div className="flex items-start justify-between gap-12">
                  <h3 className="text-s14 font-w600 text-text-strong">{team.name}</h3>
                  <Badge tone={teamStatusTone(team.status)}>{teamStatusLabel(team.status)}</Badge>
                </div>
                <dl className="grid grid-cols-2 gap-16">
                  <div>
                    <dt className="text-s12 text-text-secondary">Supporters</dt>
                    <dd className="mt-4 text-s14 font-w600 tabular-nums">{team.member_count}</dd>
                  </div>
                  <div>
                    <dt className="text-s12 text-text-secondary">Raised</dt>
                    <dd className="mt-4 text-s14 font-w600 tabular-nums">
                      {displayP2pMoney(team.raised_amount)}
                    </dd>
                  </div>
                </dl>
                {allowSettings && (
                  <div>
                    <TeamActions
                      team={team}
                      index={team.rowIndex + teamRows.length}
                      mobile
                      onAction={openTeamDialog}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
          {teamRows.length === 0 && <p className="text-s14 text-text-secondary">No teams yet.</p>}
        </div>
      </section>
      <section className="flex flex-col gap-12" data-testid={`${PREFIX}-supporters-card`}>
        <h2 className="text-s16 font-w600 text-text-strong">Sub-fundraisers</h2>
        <div className="hidden lg:block">
          <DataTable
            testId="p2p-supporter-roster"
            columns={supporterColumns}
            rows={overview.supporters}
            rowKey={(supporter) => supporter.supporter_id}
            rowTestId={(supporter) => `p2p-supporter-row-${supporter.supporter_id}`}
            emptyState={
              <EmptyState
                testId={`${PREFIX}-supporters-empty`}
                title="No supporters yet"
                description={
                  ended ? undefined : 'Share the join link to invite the first supporter.'
                }
              />
            }
          />
        </div>
        <div className="flex flex-col gap-12 lg:hidden" data-testid="p2p-supporter-cards">
          {overview.supporters.map((supporter) => (
            <Card
              key={supporter.supporter_id}
              flush
              testId={`p2p-supporter-card-${supporter.supporter_id}`}
            >
              <CardBody className="flex flex-col gap-16 p-16">
                <div className="flex items-start justify-between gap-12">
                  <h3 className="text-s14 font-w600 text-text-strong">{supporter.display_name}</h3>
                  <Badge tone={supporterStatusTone(supporter.status)}>
                    {supporterStatusLabel(supporter.status)}
                  </Badge>
                </div>
                <p className="text-s12 text-text-secondary">
                  {supporter.team_name ? `Team: ${supporter.team_name}` : 'Solo'} ·{' '}
                  {supporterSourceLabel(supporter.source)}
                </p>
                <dl className="grid grid-cols-2 gap-16">
                  <div>
                    <dt className="text-s12 text-text-secondary">Raised</dt>
                    <dd className="mt-4 text-s14 font-w600 tabular-nums">
                      {displayP2pMoney(supporter.raised_amount)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-s12 text-text-secondary">Donors</dt>
                    <dd className="mt-4 text-s14 font-w600 tabular-nums">
                      {supporter.donor_count}
                    </dd>
                  </div>
                </dl>
                <div className="flex flex-col gap-12 border-t border-border-subtle pt-12">
                  <div className="text-s12 text-text-secondary">
                    <SupporterPageState supporter={supporter} />
                    <p>Joined {displayP2pDate(supporter.joined_at)}</p>
                  </div>
                  {supporterActions(supporter, true)}
                </div>
              </CardBody>
            </Card>
          ))}
          {overview.supporters.length === 0 && (
            <p className="text-s14 text-text-secondary">No supporters yet.</p>
          )}
        </div>
      </section>
      <Card flush testId={`${PREFIX}-leaderboard-card`}>
        <CardHeader
          title="Team leaderboard preview"
          subtitle="Shown on the public campaign page."
        />
        <CardBody flush>
          {leaderboardQuery.isPending ? (
            <FundraisersLoading />
          ) : leaderboardQuery.isError ? (
            <EmptyState
              testId={`${PREFIX}-leaderboard-error`}
              title="Public leaderboard isn’t available"
              action={
                <Button
                  appearance="saas"
                  tone="secondary"
                  testId={`${PREFIX}-leaderboard-retry`}
                  onClick={() => void leaderboardQuery.refetch()}
                >
                  Try again
                </Button>
              }
            />
          ) : leaderboardQuery.data && leaderboardQuery.data.length > 0 ? (
            <div data-testid={`${PREFIX}-leaderboard`}>
              <div className="hidden lg:block">
                <DataTable
                  testId={`${PREFIX}-leaderboard-table`}
                  columns={LEADERBOARD_COLUMNS}
                  rows={leaderboardQuery.data}
                  rowKey={(team) => team.team_id}
                  rowTestId={(team) => `${PREFIX}-leaderboard-row-${team.team_id}`}
                />
              </div>
              <div
                className="flex flex-col divide-y divide-border-subtle lg:hidden"
                data-testid={`${PREFIX}-leaderboard-cards`}
              >
                {leaderboardQuery.data.map((team) => (
                  <div
                    key={team.team_id}
                    className="flex flex-col gap-16 p-16"
                    data-testid={`${PREFIX}-leaderboard-card-${team.team_id}`}
                  >
                    <div className="flex items-start justify-between gap-12">
                      <h3 className="text-s14 font-w600 text-text-strong">{team.name}</h3>
                      <span className="shrink-0 text-s12 text-text-secondary tabular-nums">
                        Rank {team.rank}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-12">
                      <span className="text-s12 text-text-secondary">Raised</span>
                      <span className="text-s14 font-w600 tabular-nums">
                        {displayP2pMoney(team.raised_amount)}
                      </span>
                    </div>
                    <dl className="grid grid-cols-2 gap-16 border-t border-border-subtle pt-12">
                      <div>
                        <dt className="text-s12 text-text-secondary">Donors</dt>
                        <dd className="mt-4 text-s14 font-w600 tabular-nums">{team.donor_count}</dd>
                      </div>
                      <div>
                        <dt className="text-s12 text-text-secondary">Supporters</dt>
                        <dd className="mt-4 text-s14 font-w600 tabular-nums">
                          {team.member_count}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState
              testId={`${PREFIX}-leaderboard-empty`}
              icon={<Icon icon={Users} size={20} aria-hidden />}
              title="No ranked teams yet"
            />
          )}
        </CardBody>
      </Card>
      {allowSettings && <CampaignInvites campaignId={campaignId} />}
      {allowSettings && (
        <TeamManagementDialog
          dialog={teamDialog}
          draftStash={teamDraft}
          busy={createTeam.isPending || updateTeam.isPending}
          onOpenChange={(open) => {
            if (!open && !createTeam.isPending && !updateTeam.isPending) closeTeamDialog();
          }}
          onConfirm={confirmTeamDialog}
        />
      )}
      {allowWrites && (
        <SupporterStatusDialog
          dialog={supporterDialog}
          busy={updateSupporter.isPending}
          onOpenChange={(open) => {
            if (!open && !updateSupporter.isPending) setSupporterDialog(null);
          }}
          onConfirm={() => {
            if (allowWrites && supporterDialog && !updateSupporter.isPending)
              updateSupporter.mutate(supporterDialog);
          }}
        />
      )}
    </div>
  );
}

function SupporterPageState({ supporter }: { supporter: P2pManagedSupporterOut }) {
  if (supporter.status === 'active' && supporter.page_active === true) return null;
  return (
    <span className="text-s12 text-text-secondary">
      Fundraiser page: {pageActiveLabel(supporter.page_active)}
    </span>
  );
}

function ConfigSwitch({
  label,
  field,
  checked,
  canManage,
  pending,
  disabled = false,
  onChange,
}: {
  label: string;
  field: 'teams_enabled' | 'supporter_teams_allowed' | 'join_open';
  checked: boolean;
  canManage: boolean;
  pending: boolean;
  /** A switch that can't change now, whatever the write in flight: its parent setting is off. */
  disabled?: boolean;
  onChange: (patch: P2pConfigRequest) => void;
}) {
  return (
    <div className="flex min-h-44 items-center justify-between gap-16 lg:min-h-0">
      <p className="text-s14 text-text-secondary">{label}</p>
      {canManage ? (
        <Switch
          appearance="saas"
          checked={checked}
          disabled={pending || disabled}
          aria-label={label}
          testId={`p2p-config-${field}`}
          onCheckedChange={(value) => onChange({ [field]: value })}
        />
      ) : (
        <Badge tone={checked ? 'success' : 'neutral'}>{checked ? 'On' : 'Off'}</Badge>
      )}
    </div>
  );
}

/** The line under the join setting's question: it governs other organizations' campaigns too. */
function JoinPolicyScope({ className }: { className?: string }) {
  return (
    <p
      className={cn('mt-2 text-s13 leading-h15 text-text-secondary', className)}
      data-testid="p2p-join-policy-scope"
    >
      {REQUESTS_COPY.whoCanJoinScope}
    </p>
  );
}

/** A policy this page can name (a later server value reads as its token, and can't be chosen here). */
function knownJoinPolicy(value: string): value is JoinPolicyValue {
  return (JOIN_POLICY_ORDER as readonly string[]).includes(value);
}

/**
 * "Who can create a supporter page?" (FCM-WEB33, D-FCM-27; the creation wizard's words since D-EMC-19):
 * the campaign's ONE join setting, for people's pages and other campaigns' layers alike, as labelled
 * choices under one line saying it covers other organizations' campaigns too. The choices are the ones
 * the server offers (`join_policy_options`; "Members only" only where an owner has a member roster, the
 * OQ-2 ruling), in the ruled order. Choosing saves.
 */
export function JoinPolicy({
  config,
  canManage,
  pending,
  pendingValue = null,
  membersWords = null,
  layersShown = false,
  onChange,
}: {
  config: P2pConfigOut;
  canManage: boolean;
  pending: boolean;
  /** The choice being saved, shown chosen until the server answers. */
  pendingValue?: string | null;
  /** Who "Members only" admits, in the host's words. */
  membersWords?: string | null;
  /** Layers serve here: the line about the organization's own campaigns points at their tab. */
  layersShown?: boolean;
  onChange: (patch: P2pConfigRequest) => void;
}) {
  const offered = JOIN_POLICY_ORDER.filter((value) => config.join_policy_options?.includes(value));
  if (offered.length === 0)
    return (
      <div className="text-s14">
        <p className="text-text-secondary">{REQUESTS_COPY.whoCanJoin}</p>
        <JoinPolicyScope className="mb-4" />
        <p data-testid="p2p-join-policy-read">
          {knownJoinPolicy(config.join_policy)
            ? REQUESTS_COPY.policyLabel[config.join_policy]
            : teamStatusLabel(config.join_policy)}
        </p>
      </div>
    );
  const describe = (value: JoinPolicyValue) =>
    value === 'members_only'
      ? membersWords
        ? REQUESTS_COPY.membersOnlyDescription(membersWords)
        : undefined
      : REQUESTS_COPY.policyDescription[value];
  return (
    <div className="flex flex-col gap-8">
      <div>
        <p className="text-s14 text-text-secondary">{REQUESTS_COPY.whoCanJoin}</p>
        <JoinPolicyScope />
      </div>
      <RadioTileGroup
        value={pendingValue ?? config.join_policy}
        onValueChange={(value) => {
          if (
            canManage &&
            !pending &&
            value !== config.join_policy &&
            knownJoinPolicy(value) &&
            offered.includes(value)
          )
            onChange({ join_policy: value as P2pJoinPolicy });
        }}
        disabled={!canManage || pending}
        aria-label={REQUESTS_COPY.whoCanJoin}
        testId="p2p-join-policy"
      >
        {offered.map((value) => (
          <RadioTile
            key={value}
            value={value}
            label={REQUESTS_COPY.policyLabel[value]}
            description={describe(value)}
            testId={`p2p-join-policy-${value}`}
          />
        ))}
      </RadioTileGroup>
      {layersShown && (
        <p className="text-s13 leading-h15 text-text-secondary" data-testid="p2p-join-own-line">
          {REQUESTS_COPY.ownCampaignsLine}
        </p>
      )}
    </div>
  );
}
function TeamActions({
  team,
  index,
  onAction,
  mobile = false,
}: {
  team: ManagedP2pTeamRow;
  index: number;
  mobile?: boolean;
  onAction: (
    kind: 'rename' | 'close' | 'reopen' | 'remove' | 'reinstate',
    team: ManagedP2pTeamRow,
  ) => void;
}) {
  const normalizedStatus = team.status?.toLowerCase();
  if (!['active', 'closed', 'removed'].includes(normalizedStatus ?? '')) return null;
  if (normalizedStatus === 'removed') {
    return (
      <RowActions
        contentClassName="fundraisers-menu"
        triggerText={mobile ? 'Manage team' : undefined}
        label={`Actions for ${team.name}`}
        testId={`p2p-team-actions-${index}`}
        actions={[
          {
            label: 'Reinstate team',
            icon: <Icon icon={RotateCcw} size={14} aria-hidden />,
            testId: `p2p-team-reinstate-${index}`,
            onSelect: () => onAction('reinstate', team),
          },
        ]}
      />
    );
  }

  const closed = normalizedStatus === 'closed';
  const canClose = normalizedStatus === 'active';
  const canReopen = closed;
  return (
    <RowActions
      contentClassName="fundraisers-menu"
      triggerText={mobile ? 'Manage team' : undefined}
      label={`Actions for ${team.name}`}
      testId={`p2p-team-actions-${index}`}
      actions={[
        {
          label: 'Rename team',
          icon: <Icon icon={Pencil} size={14} aria-hidden />,
          testId: `p2p-team-rename-${index}`,
          onSelect: () => onAction('rename', team),
        },
        ...(canClose
          ? [
              {
                label: 'Close team',
                icon: <Icon icon={XCircle} size={14} aria-hidden />,
                testId: `p2p-team-close-${index}`,
                onSelect: () => onAction('close', team),
              },
            ]
          : []),
        ...(canReopen
          ? [
              {
                label: 'Reopen team',
                icon: <Icon icon={RotateCcw} size={14} aria-hidden />,
                testId: `p2p-team-reopen-${index}`,
                onSelect: () => onAction('reopen', team),
              },
            ]
          : []),
        {
          label: 'Remove team',
          destructive: true,
          icon: <Icon icon={Trash2} size={14} aria-hidden />,
          testId: `p2p-team-remove-${index}`,
          onSelect: () => onAction('remove', team),
        },
      ]}
    />
  );
}

function TeamManagementDialog({
  dialog,
  draftStash,
  busy,
  onOpenChange,
  onConfirm,
}: {
  dialog: TeamDialog;
  /** The page's copy of the draft typed into this open, kept across a remount of this dialog. */
  draftStash: RefObject<StashedTeamDraft | null>;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (draft: TeamDraft) => void;
}) {
  // The draft lives here, so a keystroke re-renders this dialog alone, never both tables, both
  // layouts and the leaderboard (FCM-WEB14). Each open resets it during the render that opens the
  // dialog — a rename from the team's name, everything else blank — which is React's "adjust state
  // when a prop changes" pattern: the dialog never commits the previous draft, where an effect would
  // commit it and then correct it. Mounting again inside the same open restores the page's copy.
  const [draftFor, setDraftFor] = useState<TeamDialog>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  if (dialog !== draftFor) {
    setDraftFor(dialog);
    if (dialog) {
      const stashed = draftStash.current;
      const kept = stashed !== null && stashed.dialog === dialog ? stashed.draft : null;
      setName(kept?.name ?? (dialog.kind === 'rename' ? dialog.team.name : ''));
      setDescription(kept?.description ?? '');
    }
  }
  function edit(draft: TeamDraft) {
    if (dialog) draftStash.current = { dialog, draft };
    setName(draft.name);
    setDescription(draft.description);
  }
  const kind = dialog?.kind;
  const title =
    kind === 'create'
      ? 'Create a team'
      : kind === 'rename'
        ? 'Rename this team'
        : kind === 'close'
          ? 'Close this team?'
          : kind === 'reopen'
            ? 'Reopen this team?'
            : kind === 'remove'
              ? 'Remove this team?'
              : 'Reinstate this team?';
  const body =
    kind === 'close'
      ? 'Existing members stay on the team, but new supporters can’t join it.'
      : kind === 'reopen'
        ? 'Reopening makes this team eligible for new members when campaign settings allow.'
        : kind === 'remove'
          ? 'Removing this team means members drop to solo; the team name stays reserved. Supporters and donations are untouched.'
          : kind === 'reinstate'
            ? 'Reinstating makes the team eligible to appear and accept new members when campaign settings allow. It does not reattach former members.'
            : null;
  const confirmLabel =
    kind === 'create'
      ? 'Create team'
      : kind === 'rename'
        ? 'Save name'
        : kind === 'close'
          ? 'Close team'
          : kind === 'reopen'
            ? 'Reopen team'
            : kind === 'remove'
              ? 'Remove team'
              : 'Reinstate team';
  const formKind = kind === 'create' || kind === 'rename';
  const confirmDisabled = formKind && name.trim().length === 0;

  return (
    <Dialog.Root open={dialog !== null} onOpenChange={onOpenChange}>
      <Dialog.Content
        aria-label={title}
        className="fundraisers-dialog flex flex-col gap-16"
        testId={kind === 'create' ? 'p2p-team-create-dialog' : 'p2p-team-dialog'}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        {formKind ? (
          <div className="flex flex-col gap-12">
            <TextField
              label="Team name"
              value={name}
              maxLength={100}
              disabled={busy}
              onChange={(event) => edit({ name: event.target.value, description })}
              testId="p2p-team-name"
            />
            {kind === 'create' ? (
              <Textarea
                label="Description (optional)"
                rows={3}
                value={description}
                disabled={busy}
                onChange={(event) => edit({ name, description: event.target.value })}
                testId="p2p-team-description"
              />
            ) : null}
          </div>
        ) : (
          <p className="text-s13 text-text-secondary">{body}</p>
        )}
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={busy}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp('p2p-team-dialog-cancel')}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone={kind === 'remove' ? 'danger' : 'primary'}
            size="md"
            loading={busy}
            disabled={confirmDisabled}
            onClick={() => onConfirm({ name, description })}
            testId={kind === 'create' ? 'p2p-team-create-confirm' : 'p2p-team-dialog-confirm'}
          >
            {confirmLabel}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function SupporterStatusDialog({
  dialog,
  busy,
  onOpenChange,
  onConfirm,
}: {
  dialog: SupporterDialog | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const removing = dialog?.nextStatus === 'removed';
  return (
    <Dialog.Root open={dialog !== null} onOpenChange={onOpenChange}>
      <Dialog.Content
        aria-label={removing ? 'Detach supporter' : 'Reinstate supporter'}
        className="fundraisers-dialog flex flex-col gap-16"
        testId="p2p-supporter-status-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {removing ? 'Detach this supporter?' : 'Reinstate this supporter?'}
        </Dialog.Title>
        <p className="text-s13 text-text-secondary">
          {removing
            ? 'This removes their page from the public campaign; their fundraiser and donations are untouched; reversible.'
            : 'This restores their moderation status. Whether the page is public still depends on its page state and campaign settings. Their fundraiser and donations are untouched.'}
        </p>
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={busy}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp('p2p-supporter-status-cancel')}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone={removing ? 'danger' : 'primary'}
            size="md"
            loading={busy}
            onClick={onConfirm}
            testId="p2p-supporter-status-confirm"
          >
            {removing ? 'Detach supporter' : 'Reinstate supporter'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
